import React, { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Bot, Check } from "lucide-react";
import {
  PHRASE_INTERVAL_MS,
  SLOW_THRESHOLD_MS,
  THINKING_PHRASES,
  THINKING_STEPS,
  activeStepIndex,
} from "../../utils/mealChatThinking";

// Burbuja que ocupa el lugar de la respuesta mientras el agente trabaja y aún
// no ha emitido texto. Se monta al empezar a pensar y se desmonta con el
// primer token, así que su propio reloj mide cuánto lleva esperando el atleta.
const ThinkingIndicator = ({ calls }) => {
  const reduceMotion = useReducedMotion();
  const [startedAt] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);
  const [phraseIndex] = useState(() => Math.floor(Math.random() * THINKING_PHRASES.length));

  useEffect(() => {
    const timer = setInterval(() => setElapsed(Date.now() - startedAt), 200);
    return () => clearInterval(timer);
  }, [startedAt]);

  const active = activeStepIndex(elapsed, calls);
  const isSlow = elapsed >= SLOW_THRESHOLD_MS;
  const phrase = isSlow
    ? "Está tardando un poco más de lo normal, sigo trabajando…"
    : THINKING_PHRASES[(phraseIndex + Math.floor(elapsed / PHRASE_INTERVAL_MS)) % THINKING_PHRASES.length];
  // El último paso queda "en curso" hasta que llega el texto: la barra se
  // acerca al final sin llegar nunca.
  const progress = Math.min(0.94, (active + 0.6) / THINKING_STEPS.length);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6, transition: { duration: 0.15 } }}
      className="flex justify-start"
    >
      <div className="flex items-start gap-3 w-full max-w-[95%] sm:max-w-sm">
        <div className="relative w-8 h-8 shrink-0">
          <span className="absolute inset-0 rounded-full bg-athletain-green/30 animate-ping motion-reduce:hidden" />
          <div className="relative w-8 h-8 rounded-full flex items-center justify-center bg-athletain-green text-white">
            <Bot size={16} />
          </div>
        </div>

        <div className="flex-1 min-w-0 overflow-hidden rounded-lg bg-gray-100">
          <div className="px-4 pt-3 pb-2">
            <div className="min-h-5" aria-hidden="true">
              <AnimatePresence mode="wait" initial={false}>
                <motion.p
                  key={phrase}
                  initial={{ opacity: 0, y: reduceMotion ? 0 : 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: reduceMotion ? 0 : -6 }}
                  transition={{ duration: 0.25 }}
                  className="chat-shimmer text-sm leading-5 font-medium"
                >
                  {phrase}
                </motion.p>
              </AnimatePresence>
            </div>

            <ol className="mt-2.5 space-y-1.5">
              {THINKING_STEPS.map((step, i) => {
                const state = i < active ? "done" : i === active ? "active" : "pending";
                return (
                  <li key={step.id} className="flex items-start gap-2.5 text-[13px]">
                    <span className="relative mt-px flex h-4 w-4 shrink-0 items-center justify-center">
                      <AnimatePresence initial={false}>
                        {state === "done" ? (
                          <motion.span
                            key="done"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 500, damping: 22 }}
                            className="absolute inset-0 flex items-center justify-center rounded-full bg-athletain-green text-white"
                          >
                            <Check size={10} strokeWidth={3.5} />
                          </motion.span>
                        ) : state === "active" ? (
                          <span
                            key="active"
                            className="absolute inset-0 rounded-full border-2 border-athletain-green/25 border-t-athletain-green animate-spin motion-reduce:animate-none"
                          />
                        ) : (
                          <span key="pending" className="h-1.5 w-1.5 rounded-full bg-gray-300" />
                        )}
                      </AnimatePresence>
                    </span>
                    <span
                      className={`leading-snug transition-colors duration-300 ${
                        state === "active"
                          ? "font-medium text-gray-900"
                          : state === "done"
                            ? "text-gray-500"
                            : "text-gray-400"
                      }`}
                    >
                      {step.label}
                    </span>
                  </li>
                );
              })}
            </ol>
          </div>

          <div className="flex items-center gap-2 px-4 pb-2.5 pt-1">
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-gray-200">
              <motion.div
                className="h-full rounded-full bg-athletain-green"
                initial={{ width: "4%" }}
                animate={{ width: `${progress * 100}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </div>
            <span className="text-[11px] tabular-nums text-gray-400">{Math.floor(elapsed / 1000)} s</span>
          </div>

          <span className="sr-only" role="status">
            {THINKING_STEPS[active].label}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default ThinkingIndicator;
